import { HiOutlineMinus, HiOutlinePlus } from 'react-icons/hi';

import {
  balanceFloatDecimals,
  formatCurrency,
  removeDemicals,
} from 'helpers/convertNumber';

import {
  QtyStepperWrapper,
  QtyStepperButton,
  QtyStepperInput,
} from './MultiImport.style';

type Props = {
  value: string;
  ariaLabel?: string;
  width?: number;
  fieldProperty?: string;
  numOfDecimals?: number;
  onChange: (value: string) => void;
};

// Parse a display value that uses comma as the decimal separator (same
// convention as the detail fields) into a JS number.
function parseQty(value: string) {
  const parsed = parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

// Format a number back to the comma-based display value the detail store
// expects (comma decimal separator, same as the detail fields).
function formatValue(num: number, isInteger: boolean, decimals: number) {
  if (isInteger) return String(Math.trunc(num));
  return balanceFloatDecimals(
    formatCurrency(String(num).replace('.', ','), decimals),
    decimals,
  );
}

function QtyStepper({
  value,
  ariaLabel,
  width,
  fieldProperty,
  numOfDecimals,
  onChange,
}: Props) {
  const isInteger = fieldProperty === 'Integer';
  // Decimal fields default to 2 decimals when NUMOFDECIMAL is empty/0/missing.
  const configuredDecimals = Number(numOfDecimals);
  const decimals = isInteger ? 0 : configuredDecimals > 0 ? configuredDecimals : 2;

  const step = (operation: 'add' | 'subtract') => {
    const current = parseQty(value);
    const next = operation === 'add' ? current + 1 : current - 1;
    onChange(formatValue(next < 0 ? 0 : next, isInteger, decimals));
  };

  // Allow only digits and a single comma while typing (matches detail fields
  // which use comma as decimal separator); integers keep digits only.
  const handleInputChange = (raw: string) => {
    if (isInteger) {
      onChange(raw.replace(/[^\d]/g, ''));
      return;
    }
    let sanitized = raw.replace(/[^\d,]/g, '');
    const firstComma = sanitized.indexOf(',');
    if (firstComma >= 0) {
      sanitized =
        sanitized.slice(0, firstComma + 1) +
        sanitized.slice(firstComma + 1).replace(/,/g, '');
    }
    onChange(sanitized);
  };

  // On blur, normalize using the same helpers as the detail fields so the
  // stored value matches exactly (comma decimals, limited to `decimals`).
  const handleBlur = () => {
    if (value === '' || value == null) return;
    if (isInteger) {
      onChange(removeDemicals(formatCurrency(value, 0)));
      return;
    }
    onChange(balanceFloatDecimals(formatCurrency(value, decimals), decimals));
  };

  return (
    <QtyStepperWrapper onClick={(e) => e.stopPropagation()}>
      <QtyStepperButton
        type='button'
        aria-label='qty-subtract'
        onClick={() => step('subtract')}
      >
        <HiOutlineMinus />
      </QtyStepperButton>
      <QtyStepperInput
        type='text'
        inputMode={isInteger ? 'numeric' : 'decimal'}
        aria-label={ariaLabel}
        value={value}
        $width={width}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
      />
      <QtyStepperButton type='button' aria-label='qty-add' onClick={() => step('add')}>
        <HiOutlinePlus />
      </QtyStepperButton>
    </QtyStepperWrapper>
  );
}

export default QtyStepper;
